import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function hexToRgba(hex: string, alpha: number) {
  // Remove o '#' se existir
  hex = hex.replace(/^#/, '');

  // Se for formato curto (#FFF), expande para #FFFFFF
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  const blend = (channel: number) => Math.round(channel * alpha + 255 * (1 - alpha));

  const toHex = (value: number) => value.toString(16).padStart(2, '0').toUpperCase();

  const rNew = toHex(blend(r));
  const gNew = toHex(blend(g));
  const bNew = toHex(blend(b));

  return `#${rNew}${gNew}${bNew}`;
}

// Creates a dark variant by blending the color with dark gray while preserving saturation
export function hexToDarkRgba(hex: string, alpha: number) {
  hex = hex.replace(/^#/, '');

  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  // Use a darker base (15 instead of 30) and preserve more of the original color (higher alpha effect)
  // This keeps saturation while making it darker
  const darkBase = 15;
  const saturationPreservation = 0.7; // Preserve 70% of the color's vibrancy

  const blend = (channel: number) => {
    const darkened = channel * saturationPreservation; // Darken while preserving saturation
    return Math.round(darkened * alpha + darkBase * (1 - alpha));
  };

  const toHex = (value: number) => value.toString(16).padStart(2, '0').toUpperCase();

  const rNew = toHex(blend(r));
  const gNew = toHex(blend(g));
  const bNew = toHex(blend(b));

  return `#${rNew}${gNew}${bNew}`;
}

// Darkens a color by a percentage (for light theme hover)
export function darkenColor(hex: string, percent: number) {
  hex = hex.replace(/^#/, '');

  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  const darken = (channel: number) => Math.round(channel * (1 - percent / 100));

  const toHex = (value: number) => value.toString(16).padStart(2, '0').toUpperCase();

  const rNew = toHex(darken(r));
  const gNew = toHex(darken(g));
  const bNew = toHex(darken(b));

  return `#${rNew}${gNew}${bNew}`;
}

// Lightens a color by a percentage (for dark theme hover)
export function lightenColor(hex: string, percent: number) {
  hex = hex.replace(/^#/, '');

  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  const lighten = (channel: number) => Math.round(channel + (255 - channel) * (percent / 100));

  const toHex = (value: number) => value.toString(16).padStart(2, '0').toUpperCase();

  const rNew = toHex(lighten(r));
  const gNew = toHex(lighten(g));
  const bNew = toHex(lighten(b));

  return `#${rNew}${gNew}${bNew}`;
}

export async function urlToFile(url: string, filename: string, mimeType?: string): Promise<File | null> {
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    const type = mimeType || blob.type;
    return new File([blob], filename, { type });
  } catch {
    // CORS error or network failure - return null silently
    return null;
  }
}

export function formatCNPJ(cnpj: string) {
  cnpj = cnpj.replace(/\D/g, '');

  if (cnpj.length === 14) {
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }

  return cnpj;
}

export function formatCNPJWithoutMask(cnpj: string) {
  if (cnpj.length < 6) cnpj.replace(/^(\d{2})(\d{3})$/, '$1.$2');
  else if (cnpj.length < 10) cnpj.replace(/^(\d{2})(\d{3})(\d{4})$/, '$1.$2.$3');
  else if (cnpj.length < 14) cnpj.replace(/^(\d{2})(\d{3})(\d{4})(\d{4})$/, '$1.$2.$3/$4');
  else if (cnpj.length < 18) cnpj.replace(/^(\d{2})(\d{3})(\d{4})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');

  return cnpj;
}

/**
 * Formata CNPJ em tempo real conforme o usuário digita
 * @param value - Valor digitado pelo usuário
 * @returns CNPJ formatado
 */
export function formatCNPJRealTime(value: string): string {
  // Remove todos os caracteres não numéricos
  const numbers = value.replace(/\D/g, '');

  // Limita a 14 dígitos
  const limitedNumbers = numbers.slice(0, 14);

  // Aplica a formatação baseada no número de dígitos
  if (limitedNumbers.length <= 2) {
    return limitedNumbers;
  } else if (limitedNumbers.length <= 5) {
    return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2)}`;
  } else if (limitedNumbers.length <= 8) {
    return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5)}`;
  } else if (limitedNumbers.length <= 12) {
    return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5, 8)}/${limitedNumbers.slice(8)}`;
  } else {
    return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5, 8)}/${limitedNumbers.slice(8, 12)}-${limitedNumbers.slice(12)}`;
  }
}

/**
 * Remove a formatação do CNPJ, retornando apenas os números
 * @param cnpj - CNPJ formatado
 * @returns Apenas os números do CNPJ
 */
export function unformatCNPJ(cnpj: string): string {
  return cnpj.replace(/\D/g, '');
}

/**
 * Valida se o CNPJ está completo e é válido
 * @param cnpj - CNPJ formatado ou não
 * @returns true se o CNPJ for válido
 */
export function isValidCNPJ(cnpj: string): boolean {
  const numbers = unformatCNPJ(cnpj);

  if (numbers.length !== 14) return false;

  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1{13}$/.test(numbers)) return false;

  // Validação dos dígitos verificadores
  let sum = 0;
  let weight = 2;

  // Primeiro dígito verificador
  for (let i = 11; i >= 0; i--) {
    sum += parseInt(numbers[i]) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }

  const firstDigit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (parseInt(numbers[12]) !== firstDigit) return false;

  // Segundo dígito verificador
  sum = 0;
  weight = 2;

  for (let i = 12; i >= 0; i--) {
    sum += parseInt(numbers[i]) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }

  const secondDigit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (parseInt(numbers[13]) !== secondDigit) return false;

  return true;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Apply theme colors to the document based on a primary color
 * This sets CSS custom properties for the theme
 */
export function applyThemeColor(themeColor: string, isDarkMode: boolean = false): void {
  const secondary = hexToRgba(themeColor, 0.1);
  const secondaryDark = hexToDarkRgba(themeColor, 0.1);

  document.documentElement.style.setProperty('--primary', themeColor);
  document.documentElement.style.setProperty('--secondary-light', secondary);
  document.documentElement.style.setProperty('--secondary-light-hover', darkenColor(secondary, 10));

  if (isDarkMode) {
    document.documentElement.style.setProperty('--secondary', secondaryDark);
    document.documentElement.style.setProperty('--secondary-hover', lightenColor(secondaryDark, 10));
  } else {
    document.documentElement.style.setProperty('--secondary', secondary);
    document.documentElement.style.setProperty('--secondary-hover', darkenColor(secondary, 10));
  }

  document.documentElement.style.setProperty('--secondary-dark', secondaryDark);
  document.documentElement.style.setProperty('--secondary-dark-hover', lightenColor(secondaryDark, 10));
}

/**
 * Apply favicon to the document
 */
export function applyFavicon(faviconUrl: string): void {
  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = faviconUrl;
}
